import { useMemo, useRef } from "react"
import type { FC, ReactElement, RefObject } from "react"

import { QueryClient, useQueryClient } from "@tanstack/react-query"
import { motion } from "framer-motion"
import {
    type Location,
    type NavigateFunction,
    type Params,
    useLocation,
    useNavigate,
    useParams
} from "react-router-dom"

import { ArrowIcon } from "@/3514/assets"
import { Button } from "@/3514/components/shared/button/button.component"
import { HeaderMetaAssistant } from "@/3514/components/shared/header-meta/header-meta.assistant.component"
import { Typography } from "@/3514/components/shared/typography/typography.component"
import { ETypographyColor, ETypographySize } from "@/3514/components/shared/typography/typography.types"
import { EPath, ERoute } from "@/3514/constants"
import { assistantParticipantsQueryInvalidator } from "@/3514/pages/assistant.participants"
import { THEME } from "@/3514/theme"
import { EUserRole } from "@/3514/types"
import { ERoundDirection, getCurrentRouteFromPathname, withPressHandler } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { emptyAsyncCallback } from "@/shared/types/functions"

import Logo from "./headers.elements.logo"
import Profile from "./headers.elements.profile"
import classes from "./headers.styles"

const AssistantHeader: FC = (): ReactElement => {
    const dropdownRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const params: Readonly<Params> = useParams()

    const navigate: NavigateFunction = useNavigate()

    const { t }: IUseTranslation = useTranslation()

    const { pathname }: Location = useLocation()

    const qc: QueryClient = useQueryClient()

    const currentRoute: ERoute = useMemo<ERoute>((): ERoute => getCurrentRouteFromPathname(pathname), [pathname])

    const isRootPage: boolean = currentRoute === ERoute.Assistant_Participants

    function renderBackButton(): ReactElement {
        const _btnAction: ReturnType<typeof withPressHandler> = withPressHandler(
            async (): Promise<void> => await navigate(EPath.Assistant_Participants())
        )

        async function _prefetcher(): Promise<void> {
            if (!params.id) return

            await assistantParticipantsQueryInvalidator({ qc })
        }

        return (
            <Button
                tabIndex={-1}
                isContainer
                className={classes[EUserRole.Assistant].backButton()}
                onClick={_btnAction}
                onKeyDown={_btnAction}
                onMouseEnter={_prefetcher}
                onFocus={_prefetcher}
            >
                <ArrowIcon direction={ERoundDirection.Left} fillColor={THEME.COLORS.gray[300]} />

                <Typography
                    text={t("common.back")}
                    size={ETypographySize.Small}
                    color={ETypographyColor.Dark}
                    className={classes[EUserRole.Assistant].backButtonText()}
                />
            </Button>
        )
    }

    return (
        <motion.header
            layout="preserve-aspect"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 0 }}
            transition={{
                duration: 0.3,
                delay: 0.05,
                type: "tween",
                ease: "easeInOut"
            }}
            className={classes[EUserRole.Assistant].header()}
        >
            <div className={classes[EUserRole.Assistant].headerContainer()}>
                {isRootPage && <Logo />}

                {!isRootPage && renderBackButton()}

                <Profile<EUserRole.Assistant>
                    role={EUserRole.Assistant}
                    dropdownRef={dropdownRef}
                    onLogoutComplete={emptyAsyncCallback}
                />
            </div>

            <HeaderMetaAssistant isVisible={!isRootPage} />
        </motion.header>
    )
}

AssistantHeader.displayName = "AssistantHeader"

export { AssistantHeader }
